import React from 'react';
import { Helmet } from 'react-helmet-async';
import { SITE, absoluteUrl } from '../../../data/siteConfig';
import { SERVICE_AREAS } from '../../../data/serviceAreas';

// Per-page title, description, canonical, Open Graph / Twitter tags and JSON-LD.
export default function Seo({ title, description, path = '/', noindex = false, jsonLd = [] }) {
    const url = absoluteUrl(path);
    const schemas = Array.isArray(jsonLd) ? jsonLd : [jsonLd];

    return (
        <Helmet>
            <title>{title}</title>
            <meta name="description" content={description} />
            <link rel="canonical" href={url} />
            <meta name="robots" content={noindex ? 'noindex,follow' : 'index,follow'} />

            <meta property="og:type" content="website" />
            <meta property="og:site_name" content={SITE.name} />
            <meta property="og:title" content={title} />
            <meta property="og:description" content={description} />
            <meta property="og:url" content={url} />
            <meta property="og:image" content={SITE.image} />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={title} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={SITE.image} />

            {schemas.map((schema, i) => (
                <script key={i} type="application/ld+json">{JSON.stringify(schema)}</script>
            ))}
        </Helmet>
    );
}

/* ───────── JSON-LD builders ───────── */

export const BUSINESS_ID = `${SITE.url}/#business`;

export function localBusinessSchema() {
    return {
        '@context': 'https://schema.org',
        '@type': ['GeneralContractor', 'HomeAndConstructionBusiness'],
        '@id': BUSINESS_ID,
        name: SITE.name,
        legalName: SITE.legalName,
        url: `${SITE.url}/`,
        logo: SITE.logo,
        image: SITE.image,
        telephone: SITE.phoneE164,
        priceRange: SITE.priceRange,
        address: { '@type': 'PostalAddress', ...SITE.address },
        openingHoursSpecification: {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
            opens: '00:00',
            closes: '23:59',
        },
        areaServed: [
            ...SITE.counties.map((c) => ({ '@type': 'AdministrativeArea', name: `${c}, FL` })),
            ...SERVICE_AREAS.map((a) => ({ '@type': 'City', name: `${a.city}, FL` })),
        ],
        hasCredential: {
            '@type': 'EducationalOccupationalCredential',
            credentialCategory: 'Florida Certified General Contractor License',
            identifier: SITE.license,
        },
        potentialAction: {
            '@type': 'ReserveAction',
            target: SITE.bookingUrl,
            name: 'Book an Appointment',
        },
        ...(SITE.sameAs.length ? { sameAs: SITE.sameAs } : {}),
    };
}

export function serviceSchema({ name, description, path, areaName }) {
    return {
        '@context': 'https://schema.org',
        '@type': 'Service',
        name,
        serviceType: name,
        description,
        url: absoluteUrl(path),
        provider: { '@id': BUSINESS_ID },
        areaServed: areaName
            ? { '@type': 'City', name: `${areaName}, FL` }
            : SITE.counties.map((c) => ({ '@type': 'AdministrativeArea', name: `${c}, FL` })),
    };
}

export function breadcrumbSchema(items) {
    return {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: items.map((item, i) => ({
            '@type': 'ListItem',
            position: i + 1,
            name: item.name,
            item: absoluteUrl(item.path),
        })),
    };
}

export function faqSchema(faqs) {
    return {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqs.map((f) => ({
            '@type': 'Question',
            name: f.question,
            acceptedAnswer: { '@type': 'Answer', text: f.answer },
        })),
    };
}
